import { contextFactory } from '@frontend/design-system';
import { KioskTypes } from '../../../types';

type ExperienceType = 'solicited' | 'public' | 'kiosk';
type DocumentType = 'form' | 'packet';

interface CommonProps {
  experienceType: ExperienceType;
  documentId: string; // form or packet id
  locationId: string;
  isPreviewMode?: boolean;
  /** True when the patron arrived via the kiosk patron-collection flow (origin=kiosk). */
  isKioskOrigin?: boolean;
}

interface SolicitedExperienceContextProps extends CommonProps {
  experienceType: 'solicited';
}

interface PublicExperienceContextProps extends CommonProps {
  experienceType: 'public';
  documentType: DocumentType;
}

interface TemplatePreviewContextProps extends CommonProps {
  experienceType: 'public';
  documentType: 'template';
}

interface KioskExperienceContextProps extends CommonProps {
  experienceType: 'kiosk';
  patronType: KioskTypes.Patron;
  documentType: DocumentType;
}

export type FillingExperienceContextProps =
  | SolicitedExperienceContextProps
  | PublicExperienceContextProps
  | TemplatePreviewContextProps
  | KioskExperienceContextProps;

export const [FillingExperienceContext, useFillingExperienceContext] = contextFactory<FillingExperienceContextProps>();

// Type-safe hook specifically for kiosk experiences
export const useKioskFillingExperienceContext = (): KioskExperienceContextProps => {
  const context = useFillingExperienceContext();

  if (context.experienceType !== 'kiosk') {
    throw new Error('useKioskFillingExperienceContext can only be used within kiosk experiences');
  }

  return context as KioskExperienceContextProps;
};

// Type-safe hook specifically for public form/packet experiences
export const usePublicFillingExperienceContext = (): PublicExperienceContextProps => {
  const context = useFillingExperienceContext();

  if (context.experienceType !== 'public' || context.documentType === 'template') {
    throw new Error('usePublicFillingExperienceContext can only be used within public form or packet experiences');
  }

  return context as PublicExperienceContextProps;
};

// Type-safe hook specifically for solicited experiences
export const useSolicitedFillingExperienceContext = (): SolicitedExperienceContextProps => {
  const context = useFillingExperienceContext();

  if (context.experienceType !== 'solicited') {
    throw new Error('useSolicitedFillingExperienceContext can only be used within solicited experiences');
  }

  return context as SolicitedExperienceContextProps;
};
